import { useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import {
  BannerNotification,
  Button,
  InlineNotification,
  Modal,
  NakedButton,
  RadioCardField,
  Tabs,
  Text,
  TextField,
  useAlert,
  useFormField,
  type ResolvedFieldProps,
} from '@frontend/design-system';
import {
  DEFAULT_SIGNATURE_FONT,
  MAX_SIGNATURE_IMAGE_BYTES,
  MAX_TYPED_SIGNATURE_LENGTH,
  SIGNATURE_FONTS,
  SIGNATURE_PNG_FILE_NAME,
  getSignatureFontFamily,
  isSignatureFont,
} from '../../signature/constants';
import { ESignType, ESignValue } from '../../signature/esign-value';
import { useSignatureFonts } from '../../signature/fonts';
import { reportSignatureError } from '../../signature/monitoring';
import { SignatureDrawArea } from '../../signature/signature-draw-area';
import { SignatureHistoryList } from '../../signature/signature-history-list';
import { ALLOWED_SIGNATURE_IMAGE_LABEL, canvasToPngFile, sniffImageType } from '../../signature/signature-image';
import { SignaturePadHandle } from '../../signature/signature-pad';
import { signatureModalFooterStyle, signatureModalStyle } from '../../signature/signature.styles';
import { createSignatureTimestamp } from '../../signature/timestamp';
import { useObjectUrl } from '../../signature/use-media-blob';
import { useSignatureUpload } from '../../signature/use-signature-upload';
import { useDynamicFormStore } from '../../store';
import {
  fontOptionTitleStyle,
  fontOptionsStyle,
  legalNoticeStyle,
  legalNoticeTextStyle,
  modalBodyStyle,
  tabPanelStyle,
  tabPanelsStyle,
  uploadDropZoneStyle,
  uploadPreviewStyle,
} from './esign-field.styles';
import { ESignPreview } from './esign-preview';

type ESignTab = 'draw' | 'upload' | 'type';

const MODAL_WIDTH = 640;

interface ESignModalProps {
  show: boolean;
  fieldKey: string;
  onClose: () => void;
  onCommit: (value: ESignValue) => void;
}

// `minWidth` = `maxWidth` pins the width so it never follows the content (e.g. the canvas resizing while opening).
export const ESignModal = ({ show, fieldKey, onClose, onCommit }: ESignModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      minWidth={MODAL_WIDTH}
      maxWidth={MODAL_WIDTH}
      disableCloseOnOverlayClick
      css={signatureModalStyle}
    >
      <ESignModalContent
        fieldKey={fieldKey}
        onClose={handleClose}
        onCommit={onCommit}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </Modal>
  );
};

interface ESignModalContentProps extends Omit<ESignModalProps, 'show'> {
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
}

const ESignModalContent = ({ fieldKey, onClose, onCommit, isSubmitting, setIsSubmitting }: ESignModalContentProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const alerts = useAlert();
  const { eSignHistory } = useDynamicFormStore(['eSignHistory']);
  const uploadSignature = useSignatureUpload(fieldKey);

  const [activeTab, setActiveTab] = useState<ESignTab>('draw');
  const [uploadError, setUploadError] = useState(false);

  const padRef = useRef<SignaturePadHandle>(null);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [imageFile, setImageFile] = useState<File>();

  const textField = useFormField({
    type: 'text',
    transformValue: (value) => (value ?? '').slice(0, MAX_TYPED_SIGNATURE_LENGTH),
  });
  const fontField = useFormField({ type: 'radio', value: DEFAULT_SIGNATURE_FONT });
  const typedText = (textField.value ?? '').trim();

  const tabId = (tab: ESignTab) => `${fieldKey}-esign-${tab}`;

  const commitImage = async (type: Extract<ESignType, 'wet' | 'image'>, getFile: () => Promise<File>) => {
    setIsSubmitting(true);
    setUploadError(false);
    try {
      const timestamp = createSignatureTimestamp();
      // Earlier attachments stay: other fields may reuse them from the eSign history.
      const mediaId = await uploadSignature(await getFile());
      onCommit({ type, data: mediaId, font_type: '', timestamp });
    } catch (error) {
      setUploadError(true);
      alerts.error(t('Failed to upload signature. Please try again.'));
      reportSignatureError(error, { feature: 'esign', operation: 'upload', fieldKey });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleESign = async () => {
    if (activeTab === 'draw') {
      const exported = padRef.current?.exportTrimmed();
      if (!exported) return;
      await commitImage('wet', () => canvasToPngFile(exported.canvas, SIGNATURE_PNG_FILE_NAME));
      return;
    }

    if (activeTab === 'upload') {
      if (imageFile) await commitImage('image', async () => imageFile);
      return;
    }

    if (!typedText) return;
    const font = isSignatureFont(fontField.value) ? fontField.value : DEFAULT_SIGNATURE_FONT;
    onCommit({ type: 'text', data: typedText, font_type: font, timestamp: createSignatureTimestamp() });
  };

  const handleSelectHistory = (value: ESignValue) => {
    if (isSubmitting) return;
    onCommit({ ...value, timestamp: createSignatureTimestamp() });
  };

  const isPrimaryDisabled =
    (activeTab === 'draw' && !hasDrawing) ||
    (activeTab === 'upload' && !imageFile) ||
    (activeTab === 'type' && !typedText);

  // Rendered inside each tab panel, between the panel's content and the legal notice.
  const historyList = (
    <SignatureHistoryList
      items={eSignHistory}
      getKey={(entry) => `${entry.type}-${entry.font_type}-${entry.data}`}
      getLabel={() => t('Use this previous signature')}
      renderItem={(entry) => <ESignPreview value={entry} fieldKey={fieldKey} compact />}
      onSelect={handleSelectHistory}
      disabled={isSubmitting}
    />
  );
  const uploadErrorNotice = uploadError && (
    <InlineNotification
      css={{ alignSelf: 'center' }}
      size='medium'
      status='error'
      message={t('Failed to upload signature. Please try again.')}
    />
  );

  return (
    <>
      <Modal.Header onClose={onClose}>{t('Electronically sign document')}</Modal.Header>
      <Modal.Body css={modalBodyStyle}>
        <Text size='medium' color='light'>
          {t('Select one of the methods below to electronically sign this document')}
        </Text>

        <Tabs
          controlledTab={tabId(activeTab)}
          onChange={(id) => {
            setActiveTab(id.replace(`${fieldKey}-esign-`, '') as ESignTab);
            setUploadError(false);
          }}
        >
          <Tabs.Bar fullWidth>
            {[
              <Tabs.Tab key='draw' id={tabId('draw')} disabled={isSubmitting}>
                {t('Draw')}
              </Tabs.Tab>,
              <Tabs.Tab key='upload' id={tabId('upload')} disabled={isSubmitting}>
                {t('Upload')}
              </Tabs.Tab>,
              <Tabs.Tab key='type' id={tabId('type')} disabled={isSubmitting}>
                {t('Type')}
              </Tabs.Tab>,
            ]}
          </Tabs.Bar>

          {/*
            Panels share one grid cell so the modal keeps the tallest panel's height on every tab. Each panel ends
            with the signature history and the legal notice so they sit right after the panel's content, and any
            spare height falls below them.
          */}
          <div css={tabPanelsStyle}>
            <Tabs.Panel controller={tabId('draw')} css={tabPanelStyle}>
              <SignatureDrawArea
                ref={padRef}
                disabled={isSubmitting}
                ariaLabel={t('Signature drawing area')}
                onDrawingChange={(drawn) => {
                  setHasDrawing(drawn);
                  if (!drawn) setUploadError(false);
                }}
              />
              {uploadErrorNotice}
              {historyList}
              <LegalNotice />
            </Tabs.Panel>

            <Tabs.Panel controller={tabId('upload')} css={tabPanelStyle}>
              <SignatureImagePicker
                file={imageFile}
                disabled={isSubmitting}
                onChange={(file) => {
                  setImageFile(file);
                  setUploadError(false);
                }}
              />
              {uploadErrorNotice}
              {historyList}
              <LegalNotice />
            </Tabs.Panel>

            <Tabs.Panel controller={tabId('type')} css={tabPanelStyle}>
              <TypedSignatureInput
                fieldKey={fieldKey}
                textField={textField}
                fontField={fontField}
                enabled={activeTab === 'type'}
              />
              {historyList}
              <LegalNotice />
            </Tabs.Panel>
          </div>
        </Tabs>
      </Modal.Body>
      <Modal.Footer
        css={signatureModalFooterStyle}
        secondary={{ label: t('Cancel'), onClick: onClose, disabled: isSubmitting }}
        primary={{
          label: t('eSign'),
          onClick: handleESign,
          disabled: isPrimaryDisabled || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </>
  );
};

const LegalNotice = () => {
  const { t } = useTranslation('digital-forms-experience');
  return (
    <BannerNotification status='warn' fullWidth css={legalNoticeStyle}>
      <Text size='medium' css={legalNoticeTextStyle}>
        {t(
          'By clicking eSign, you confirm and agree that you have thoroughly reviewed the contents of the documents you are about to sign electronically. Any method you choose here to eSign is legally binding and you are ultimately responsible for its implications.'
        )}
      </Text>
    </BannerNotification>
  );
};

interface SignatureImagePickerProps {
  file?: File;
  disabled?: boolean;
  onChange: (file: File | undefined) => void;
}

const SignatureImagePicker = ({ file, disabled, onChange }: SignatureImagePickerProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const alerts = useAlert();
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrl = useObjectUrl(file);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    // Reset so re-selecting the same file still fires a change event.
    event.target.value = '';
    if (!selected) return;

    if (selected.size > MAX_SIGNATURE_IMAGE_BYTES) {
      alerts.error(t('Please upload an image less than 12 MB'));
      return;
    }

    const type = await sniffImageType(selected);
    if (!type) {
      alerts.error(
        t('You have uploaded invalid file. Allowed image types are {{types}}', { types: ALLOWED_SIGNATURE_IMAGE_LABEL })
      );
      return;
    }

    // Re-wrap with the sniffed type so the upload never trusts the browser-reported MIME.
    onChange(new File([selected], selected.name, { type }));
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <>
      {file && previewUrl ? (
        <div css={uploadPreviewStyle}>
          <img
            src={previewUrl}
            alt={t('Uploaded signature preview')}
            css={{ maxWidth: 150, maxHeight: 100, objectFit: 'contain' }}
          />
          <Button variant='secondary' size='small' iconName='upload' disabled={disabled} onClick={openPicker}>
            {t('Replace Image')}
          </Button>
        </div>
      ) : (
        <NakedButton css={uploadDropZoneStyle} disabled={disabled} onClick={openPicker}>
          <Text size='medium' weight='bold' color='primary'>
            {t('Upload an image of your signature')}
          </Text>
          <Text size='small' color='light'>
            {t('{{types}} up to 12 MB', { types: ALLOWED_SIGNATURE_IMAGE_LABEL })}
          </Text>
        </NakedButton>
      )}
      <input ref={inputRef} type='file' accept='image/*' hidden onChange={handleFileChange} />
    </>
  );
};

interface TypedSignatureInputProps {
  fieldKey: string;
  textField: ResolvedFieldProps<'text'>;
  fontField: ResolvedFieldProps<'radio'>;
  enabled: boolean;
}

const TypedSignatureInput = ({ fieldKey, textField, fontField, enabled }: TypedSignatureInputProps) => {
  const { t } = useTranslation('digital-forms-experience');
  // Fonts are only fetched once the signer opens this tab.
  useSignatureFonts(SIGNATURE_FONTS, enabled);
  const previewText = (textField.value ?? '').trim() || t('Signature');

  return (
    <>
      <TextField
        {...textField}
        name={`${fieldKey}-typed-signature`}
        label={t('Signature')}
        helperText={t('Type Your Signature Above')}
      />
      <RadioCardField
        {...fontField}
        name={`${fieldKey}-signature-font`}
        label={t('Signature style')}
        css={fontOptionsStyle}
      >
        {SIGNATURE_FONTS.map((font) => (
          <RadioCardField.Option
            key={font}
            value={font}
            title={
              <span css={[fontOptionTitleStyle, { fontFamily: getSignatureFontFamily(font) }]}>{previewText}</span>
            }
          />
        ))}
      </RadioCardField>
    </>
  );
};
